import React from "react";
import { GeoPoint } from "@firebase/firestore";
import {
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

import { I18nKeyType } from "@/i18n";
import { useTranslate } from "@/i18n/useTranslate";

import { DsInput } from "./ui/DsInput";

type BaseFormValuesType = {
  geopoint: GeoPoint;
  address: string;
} & Record<string, unknown>;

type AddressFieldPropsType<T extends BaseFormValuesType> = {
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  errors: {
    address?: {
      type: string;
      message: string;
    };
  };
  label?: string;
  errorLabel: string;
  fieldName: Path<T>;
};

export const AddressField = <T extends BaseFormValuesType>({
  watch,
  setValue,
  register,
  errors,
  label,
  errorLabel,
  fieldName,
}: AddressFieldPropsType<T>) => {
  const loadingText = useTranslate("address_field.loading");

  const handleChange = (address: string): void => {
    setValue(fieldName, address as PathValue<T, Path<T>>);
    void (async () => {
      try {
        const results = await geocodeByAddress(address);
        const latLng = await getLatLng(results[0]);
        setValue(
          "geopoint" as Path<T>,
          new GeoPoint(latLng.lat, latLng.lng) as PathValue<T, Path<T>>,
        );
      } catch (error) {
        console.error("Error", error);
      }
    })();
  };

  const address = watch(fieldName);

  return (
    <div>
      <PlacesAutocomplete
        value={address as string}
        onChange={handleChange}
        onSelect={handleChange}
        searchOptions={{ componentRestrictions: { country: "ca" } }}>
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
          const inputProps = getInputProps();
          const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            inputProps.onBlur?.(e);
          };

          const handleInputChange = (
            e: React.ChangeEvent<HTMLInputElement>,
          ) => {
            inputProps.onChange?.(e);
          };

          return (
            <div className="relative">
              <DsInput
                {...register(fieldName)}
                placeholder={"address_field.placeholder" as I18nKeyType}
                error={errors.address?.message}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
                value={inputProps.value}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={suggestions.length > 0}
                aria-activedescendant={inputProps["aria-activedescendant"]}
              />
              {(suggestions.length > 0 || loading) && (
                <div className="absolute z-10 bg-white w-full border border-gray-300 shadow-sm">
                  {loading && <div className="p-2">{loadingText}</div>}
                  {suggestions.map(suggestion => (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className: `p-2 cursor-pointer text-sm ${
                          suggestion.active ? "bg-gray-100" : ""
                        }`,
                      })}
                      key={suggestion.placeId}>
                      {suggestion.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }}
      </PlacesAutocomplete>
    </div>
  );
};
